import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';

// IMPORTANT: backend mounts auth routes under /api. Earlier builds dropped the
// /api prefix, which silently routed the request to the React static server
// (returning HTML) and made the page appear broken with no feedback.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Forgot Password Page
 * User enters email to receive a password-reset link.
 *
 * UX guarantees:
 *  - Loading spinner is always visible while the request is in flight.
 *  - Submit is disabled while loading (prevents duplicate clicks).
 *  - EVERY exit path produces a visible toast — no silent failures.
 *  - HTML/non-JSON responses are detected and treated as a network error,
 *    so we never claim success when the request actually missed the API.
 */
const ForgotPassword = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || submitted) return; // duplicate-click guard

    const trimmed = email.trim();
    if (!trimmed) {
      toast.error(t('forgotPassword.errEmptyEmail'));
      return;
    }
    // Light e-mail shape check (matches HTML5 input but works without it)
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error(t('forgotPassword.errInvalidEmail'));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/auth/forgot-password`,
        { email: trimmed.toLowerCase() },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 15000,
          // Treat anything outside 2xx/4xx as an error so we don't accidentally
          // treat an HTML 404 page as success.
          validateStatus: (s) => s >= 200 && s < 500,
        }
      );

      // Defensive: the backend always returns { success: true, message: "..." }
      // for this endpoint. If the body isn't JSON or doesn't have that shape,
      // we hit the React static server, not the API — treat as failure.
      const data = response.data;
      const okShape = data && typeof data === 'object' && data.success === true;
      if (response.status >= 400 || !okShape) {
        const detail = data && typeof data === 'object' ? data.detail || data.message : null;
        throw new Error(typeof detail === 'string' ? detail : 'Unexpected server response');
      }

      setSubmitted(true);
      toast.success(t('forgotPassword.successToast'));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('Forgot password error:', error);
      }
      // Network errors get a clearer message so the user knows to retry.
      const isNetwork =
        error?.code === 'ECONNABORTED' ||
        error?.message === 'Network Error' ||
        !error?.response;
      toast.error(
        isNetwork
          ? t('forgotPassword.errNetwork')
          : t('forgotPassword.errGeneric')
      );
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center p-4"
        data-testid="forgot-password-success-view"
      >
        <Card className="w-full max-w-md border-2 border-green-200">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="bg-green-100 rounded-full p-4">
                  <CheckCircle2 className="h-12 w-12 text-green-600" />
                </div>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {t('forgotPassword.successTitle')}
                </h2>
                <p className="text-gray-600" data-testid="forgot-password-sent-message">
                  {t('forgotPassword.successBody', { email })}
                </p>
                <p className="text-sm text-gray-500 mt-3">
                  {t('forgotPassword.linkExpires')}
                </p>
              </div>
              <div className="space-y-3 pt-4">
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 min-h-[44px]"
                  data-testid="forgot-password-back-to-login-btn"
                >
                  {t('forgotPassword.backToLogin')}
                </Button>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  className="text-sm text-purple-600 hover:text-purple-700 underline min-h-[44px]"
                  data-testid="forgot-password-resend-btn"
                >
                  {t('forgotPassword.resendLink')}
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center p-4"
      data-testid="forgot-password-page"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-gray-600 hover:text-gray-900 min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label={t('forgotPassword.backToLogin')}
              data-testid="forgot-password-back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <CardTitle className="text-2xl">{t('forgotPassword.title')}</CardTitle>
          </div>
          <p className="text-sm text-gray-600">{t('forgotPassword.subtitle')}</p>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={handleSubmit}
            noValidate
            className="space-y-4"
            data-testid="forgot-password-form"
          >
            <div className="space-y-2">
              <Label htmlFor="forgot-email">{t('forgotPassword.emailLabel')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder={t('forgotPassword.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 min-h-[44px]"
                  autoFocus
                  autoComplete="email"
                  data-testid="forgot-password-email-input"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 min-h-[44px]"
              data-testid="forgot-password-submit-btn"
              aria-busy={loading}
            >
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('forgotPassword.sending')}
                </span>
              ) : (
                t('forgotPassword.sendResetLink')
              )}
            </Button>
            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-purple-600 hover:text-purple-700 underline"
                data-testid="forgot-password-back-link"
              >
                {t('forgotPassword.backToLogin')}
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
